<template>
  <div class="page-container">
    <PageHeader title="全局异常记录"></PageHeader>

    <SearchPanel>
      <el-input v-model.trim="query.smegInstance" placeholder="服务名" size="small" clearable></el-input>
      <el-input v-model.trim="query.smegUrl" placeholder="请求接口" size="small" clearable></el-input>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 40, 80, 160, 320]"
        :total="total"
        row-id-key="smegId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      >
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无异常记录" icon="el-icon-warning-outline"></EmptyState>
    </TablePanel>

    <!-- 异常详情弹窗 -->
    <el-dialog title="异常详情" :visible.sync="detailVisible" width="680px" append-to-body>
      <template v-if="detailRow">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="服务名">{{ detailRow.smegInstance || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="detailRow.smegStatus === 2 ? 'success' : 'danger'" size="mini">
              {{ detailRow.smegStatus === 2 ? '已处理' : '未处理' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="用户名">{{ detailRow.smegUserName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="用户ID">{{ detailRow.smegUserId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="请求接口" :span="2">{{ detailRow.smegUrl || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detailRow.createtime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="备注">{{ detailRow.smegDes || '-' }}</el-descriptions-item>
          <el-descriptions-item label="处理备注" :span="2">{{ detailRow.smegHandlerDes || '-' }}</el-descriptions-item>
        </el-descriptions>
        <div class="detail-block">
          <div class="detail-block__title">请求参数</div>
          <pre class="detail-block__content">{{ formatJson(detailRow.smegParams) }}</pre>
        </div>
        <div class="detail-block">
          <div class="detail-block__title">错误信息</div>
          <pre class="detail-block__content detail-block__content--error">{{ detailRow.smegMsg || '-' }}</pre>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getSysMsgErrorGlobalList, updateSysMsgErrorGlobalStatus } from '@/api/api'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

export default {
  name: 'GlobalErrorLogPage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      total: 0,
      list: [],
      // 列定义：使用 render 函数和 buttons 代替插槽
      columns: [
        { type: 'index', label: '序号', width: 55 },
        { prop: 'smegInstance', label: '服务名', width: 110, showTooltip: true },
        { prop: 'smegUrl', label: '请求接口', minWidth: 220, showTooltip: true },
        { prop: 'smegParams', label: '请求参数', minWidth: 180, showTooltip: true },
        { prop: 'smegMsg', label: '错误信息', minWidth: 200, showTooltip: true },
        { prop: 'smegUserName', label: '用户名', width: 80 },
        {
          prop: 'smegStatus',
          label: '状态',
          width: 120,
          render: (h, { row }) => {
            return h('el-tag', {
              props: { type: row.smegStatus === 2 ? 'success' : 'danger', size: 'mini' }
            }, row.smegStatus === 2 ? '已处理' : '未处理')
          }
        },
        { prop: 'smegHandlerDes', label: '处理备注', width: 110, showTooltip: true },
        { prop: 'createtime', label: '创建时间', width: 140 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 130,
          fixed: 'right',
          buttons: [
            { text: '详情', type: 'primary' },
            { text: '处理', type: 'primary', isShow: (row) => row.smegStatus === 2 }
          ]
        }
      ],
      query: { smegInstance: '', smegUrl: '', currentPage: 1, pageSize: 10 },
      detailVisible: false,
      detailRow: null
    }
  },
  created () { this.fetchList() },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const params = {
          smegInstance: this.query.smegInstance || undefined,
          smegUrl: this.query.smegUrl || undefined,
          currentPage: this.query.currentPage,
          pageSize: this.query.pageSize
        }
        const res = await getSysMsgErrorGlobalList(params)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    // 操作列按钮点击分发
    handleOperateBtnClick ({ btn, row }) {
      if (btn.text === '详情') {
        this.handleDetail(row)
      } else if (btn.text === '处理') {
        this.handleProcess(row)
      }
    },
    // 查看异常详情
    handleDetail (row) {
      this.detailRow = row
      this.detailVisible = true
    },
    // 标记为已处理
    async handleProcess (row) {
      try {
        const { value } = await this.$prompt('请输入处理备注（可选）', '确认处理', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPlaceholder: '请输入备注'
        })
        await updateSysMsgErrorGlobalStatus({ smegId: row.smegId, smegDes: value || '' })
        this.$message.success('处理成功')
        this.fetchList()
      } catch (e) {
        if (e !== 'cancel' && e?.toString() !== 'cancel') {
          // 用户取消不提示
        }
      }
    },
    // 格式化 JSON 字符串
    formatJson (str) {
      if (!str) return '-'
      try {
        return JSON.stringify(JSON.parse(str), null, 2)
      } catch (e) {
        return str
      }
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { smegInstance: '', smegUrl: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.detail-block {
  margin-top: 16px;

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 6px;
  }

  &__content {
    background: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    color: #606266;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
    font-family: Consolas, Monaco, 'Courier New', monospace;

    &--error {
      color: #dc2626;
      background: #fef2f2;
      border-color: #fecaca;
    }
  }
}
</style>
